import React, { useRef, useState, useMemo } from 'react';
import toast from 'react-hot-toast';
import { apiService } from '../../../services';
import { SearchDropdown } from '../../../components/shared/ui';

interface ImportCardProps {
  title: string;
  description: string;
  entities: ReadonlyArray<{ id: string; name: string }>;
  entityLabel: string;
  uploadEndpoint: string;
  isLoadingEntities: boolean;
}

interface ImportResult {
  inserted_count: number;
  updated_count: number;
  skipped_count: number;
}

/**
 * Reusable card for uploading an Excel file against a selected entity.
 * Handles entity selection, file picking, upload, and result display.
 */
export const ImportCard: React.FC<ImportCardProps> = ({
  title,
  description,
  entities,
  entityLabel,
  uploadEndpoint,
  isLoadingEntities,
}) => {
  const [selectedEntityId, setSelectedEntityId] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [lastResult, setLastResult] = useState<ImportResult | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const dropdownOptions = useMemo(
    () => entities.map((e) => ({ id: e.id, label: e.name })),
    [entities],
  );

  const selectedOption = useMemo(
    () => dropdownOptions.find((o) => o.id === selectedEntityId) ?? null,
    [dropdownOptions, selectedEntityId],
  );

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0] ?? null;
    setSelectedFile(file);
    setLastResult(null);
  };

  const handleUpload = async (): Promise<void> => {
    if (!selectedEntityId || !selectedFile) {
      return;
    }

    setIsUploading(true);
    setLastResult(null);

    try {
      const result = await apiService.uploadFile<ImportResult>(
        `${uploadEndpoint}/${selectedEntityId}`,
        selectedFile,
      );

      setLastResult(result);
      toast.success(
        `${result.inserted_count} inserted, ${result.updated_count} updated` +
          (result.skipped_count > 0 ? ` (${result.skipped_count} duplicates skipped)` : ''),
      );
      setSelectedFile(null);

      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Upload failed';
      toast.error(message);
    } finally {
      setIsUploading(false);
    }
  };

  const isUploadDisabled = !selectedEntityId || !selectedFile || isUploading;

  return (
    <div>
      <p className="text-sm text-slate-500 mb-5">{description}</p>

      {/* Entity selector */}
      <label className="block text-xs font-medium text-slate-600 mb-1">
        {entityLabel}
      </label>
      <div className="mb-4">
        <SearchDropdown
          options={dropdownOptions}
          value={selectedOption}
          onSelect={(option) => {
            setSelectedEntityId(option?.id ?? '');
            setLastResult(null);
          }}
          placeholder={isLoadingEntities ? 'Loading...' : `Select ${entityLabel.toLowerCase()}`}
          isLoading={isLoadingEntities}
          disabled={isLoadingEntities}
          inputClassName="!text-sm !py-2"
          dropdownSize="md"
        />
      </div>

      {/* File input */}
      <label className="block text-xs font-medium text-slate-600 mb-1">
        CSV File (.csv)
      </label>
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv"
        onChange={handleFileChange}
        className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800
                   file:mr-3 file:rounded-md file:border-0 file:bg-slate-100 file:px-3 file:py-1
                   file:text-xs file:font-medium file:text-slate-700 mb-4"
      />

      {/* Upload button */}
      <button
        onClick={handleUpload}
        disabled={isUploadDisabled}
        className="w-full rounded-lg bg-amber-500 px-4 py-2.5 text-sm font-semibold text-white
                   shadow-sm transition-colors duration-200
                   hover:bg-amber-600 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {isUploading ? 'Uploading...' : 'Upload & Import'}
      </button>

      {/* Result display */}
      {lastResult && (
        <div className="mt-4 rounded-lg bg-emerald-50 border border-emerald-200 px-4 py-3 text-sm text-emerald-800 space-y-1">
          <div>
            <span className="font-semibold text-emerald-700">{lastResult.inserted_count}</span>
            <span> new items inserted</span>
          </div>
          <div>
            <span className="font-semibold text-slate-700">{lastResult.updated_count}</span>
            <span className="text-slate-600"> existing items updated</span>
          </div>
          {lastResult.skipped_count > 0 && (
            <div>
              <span className="font-semibold text-amber-600">{lastResult.skipped_count}</span>
              <span className="text-slate-600"> duplicates skipped</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
